import type { LucideIcon } from "lucide-react";
import {
    Check,
    FileCheck,
    Info,
    ShieldCheck,
    Users,
} from "lucide-react";
import { DataKeys } from "../../../types";
import { useLang } from "../../Context";
import { SlideInAnim } from "../../tools/Animations";
import { HealthSection } from "../HealthSection";
import { HealthSectionHeader } from "../HealthSectionHeader";
import { getLocalized, HealthLang, LocalizedString } from "../healthTypes";
import { healthAimmaDoctorSectionContentOffset } from "../healthLayout";
import {
    healthPhysicianControlCard,
    healthPhysicianControlCardCopy,
    healthPhysicianControlCardIcon,
    healthPhysicianControlCardIndex,
    healthPhysicianControlCards,
    healthPhysicianControlCardText,
    healthPhysicianControlCardTitle,
    healthPhysicianControlNote,
    healthPhysicianControlNoteIcon,
    healthPhysicianControlNoteText,
    healthPhysicianControlPanel,
    healthPhysicianControlSectionStack,
    healthPhysicianControlStatement,
    healthPhysicianControlStatementBody,
    healthPhysicianControlStatementIcon,
    healthPhysicianControlStatementTitle,
    healthPhysicianControlTrustPill,
} from "../healthVisualStyles";

interface HealthPhysicianControlSectionProps {
    data: DataKeys;
}

type CheckpointId = "access" | "templates" | "audit";

const CHECKPOINT_ICONS: Record<CheckpointId, LucideIcon> = {
    access: Users,
    templates: FileCheck,
    audit: ShieldCheck,
};

const ICON_STROKE = 1.75;

function isCheckpointId(value: string): value is CheckpointId {
    return value in CHECKPOINT_ICONS;
}

export function HealthPhysicianControlSection({
    data,
}: HealthPhysicianControlSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const statement = data.statement as DataKeys;
    const checkpoints = (data.checkpoints as DataKeys[]) ?? [];

    return (
        <HealthSection
            id="oversight"
            ariaLabelledBy="oversight-title"
            compact
        >
            <HealthSectionHeader
                eyebrow={getLocalized(data.eyebrow as LocalizedString, hLang)}
                title={getLocalized(data.title as LocalizedString, hLang)}
                titleId="oversight-title"
                subtitle={getLocalized(data.intro as LocalizedString, hLang)}
                subtitleWide
            />

            <div className={`${healthAimmaDoctorSectionContentOffset} ${healthPhysicianControlSectionStack}`}>
                <SlideInAnim direction="bottom">
                <div className={healthPhysicianControlPanel}>
                    <div className={healthPhysicianControlStatement}>
                        <span
                            className={healthPhysicianControlStatementIcon}
                            aria-hidden="true"
                        >
                            <ShieldCheck
                                className="h-8 w-8 lg:h-9 lg:w-9"
                                strokeWidth={ICON_STROKE}
                            />
                        </span>
                        <h3 className={healthPhysicianControlStatementTitle}>
                            {getLocalized(
                                statement.title as LocalizedString,
                                hLang
                            )}
                        </h3>
                        <p className={healthPhysicianControlStatementBody}>
                            {getLocalized(
                                statement.body as LocalizedString,
                                hLang
                            )}
                        </p>
                        {statement.trustLabel ? (
                            <p className={healthPhysicianControlTrustPill}>
                                <Check
                                    className="h-4 w-4 shrink-0 text-teal-600"
                                    strokeWidth={2.25}
                                    aria-hidden="true"
                                />
                                {getLocalized(
                                    statement.trustLabel as LocalizedString,
                                    hLang
                                )}
                            </p>
                        ) : null}
                    </div>

                    <div className={healthPhysicianControlCards}>
                        {checkpoints.map((item, index) => {
                            const checkpointId = item.id as string;
                            const Icon = isCheckpointId(checkpointId)
                                ? CHECKPOINT_ICONS[checkpointId]
                                : ShieldCheck;

                            return (
                                <article
                                    key={checkpointId}
                                    className={healthPhysicianControlCard}
                                >
                                    <span className={healthPhysicianControlCardIndex}>
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                    <div className={healthPhysicianControlCardCopy}>
                                        <h4 className={healthPhysicianControlCardTitle}>
                                            {getLocalized(
                                                item.step as LocalizedString,
                                                hLang
                                            )}
                                        </h4>
                                        <p className={healthPhysicianControlCardText}>
                                            {getLocalized(
                                                item.text as LocalizedString,
                                                hLang
                                            )}
                                        </p>
                                    </div>
                                    <Icon
                                        className={healthPhysicianControlCardIcon}
                                        strokeWidth={ICON_STROKE}
                                        aria-hidden="true"
                                    />
                                </article>
                            );
                        })}
                    </div>
                </div>
                </SlideInAnim>

                <aside className={healthPhysicianControlNote}>
                    <span
                        className={healthPhysicianControlNoteIcon}
                        aria-hidden="true"
                    >
                        <Info className="h-5 w-5" strokeWidth={ICON_STROKE} />
                    </span>
                    <p className={healthPhysicianControlNoteText}>
                        {getLocalized(data.disclaimer as LocalizedString, hLang)}
                    </p>
                </aside>
            </div>
        </HealthSection>
    );
}
